export const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  if (el.getAttribute && el.getAttribute('role') === 'textbox') return true;
  return false;
};

export const resolveShortcut = (event) => {
  if (!event) return null;
  const { key, ctrlKey, metaKey, shiftKey, altKey } = event;
  if (altKey) return null;
  const mod = ctrlKey || metaKey;

  if (mod) {
    const lower = typeof key === 'string' ? key.toLowerCase() : '';
    if (lower === 'a') return { type: 'selectAll' };
    if (lower === 'g') return { type: 'toggleGrid' };
    // Any other Ctrl/Cmd combo (incl. deferred Z/Y/C/V) is not ours.
    return null;
  }

  switch (key) {
    case 'Escape':
      return { type: 'clearSelection' };
    case 'F2':
      return { type: 'editName' };
    case 'Delete':
    case 'Backspace':
      return { type: 'delete' };
    case 'ArrowUp':
      return { type: 'nudge', dx: 0, dy: -1 };
    case 'ArrowDown':
      return { type: 'nudge', dx: 0, dy: 1 };
    case 'ArrowLeft':
      return { type: 'nudge', dx: -1, dy: 0 };
    case 'ArrowRight':
      return { type: 'nudge', dx: 1, dy: 0 };
    case '+':
    case '=':
      return { type: 'zoomIn' };
    case '-':
    case '_':
      return { type: 'zoomOut' };
    case '?':
      return { type: 'help' };
    default:
      break;
  }

  const lower = typeof key === 'string' ? key.toLowerCase() : '';
  switch (lower) {
    case 'm':
      return { type: 'addRelative', relative: 'son' };
    case 'f':
      return { type: 'addRelative', relative: 'daughter' };
    case 'u':
      return { type: 'addRelative', relative: 'unknown' };
    case 'p':
      return { type: 'addRelative', relative: 'spouse' };
    case 'b':
      return { type: 'addRelative', relative: 'brother' };
    case 's':
      return { type: 'addRelative', relative: 'sister' };
    case 'a':
      return {
        type: 'addRelative',
        relative: shiftKey ? 'grandparents' : 'parents'
      };
    case 'd':
      return { type: 'panel', panel: 'diagnoses' };
    case 'n':
      return { type: 'panel', panel: 'clinicalNotes' };
    case 'g':
      return { type: 'panel', panel: 'genotypes' };
    case 'h':
      return { type: 'panel', panel: 'phenotypes' };
    case 'r':
      return { type: 'panel', panel: 'riskFactors' };
    case 'i':
      return { type: 'panel', panel: 'suggestions' };
    case 'o':
      return { type: 'panel', panel: 'formSubmission' };
    case 'w':
      return { type: 'panel', panel: 'pathways' };
    case 'y':
      return { type: 'panel', panel: 'pedigreeMatch' };
    case 'c':
      return { type: 'panel', panel: 'recordStatus' };
    case 'l':
      // Not a panel any more: legend visibility is a modal off the pedigree
      // panel (TGCLOUD-1674). Left on the same key so the shortcut a user has
      // learnt still reaches the same settings.
      return { type: 'legendConfig' };
    case 'e':
      return { type: 'panel', panel: 'export' };
    // TGCLOUD-1670. V opens the Overlays popover, following the same rule as L
    // for legend configuration: a pedigree-panel affordance gets a letter, not a
    // sidebar panel. J and K step forward and back through the current
    // overlay's matches, in the direction vim has trained every reader to
    // expect. All three keys were verified free before being taken.
    case 'v':
      return { type: 'overlays' };
    case 'j':
      return { type: 'overlayStep', delta: 1 };
    case 'k':
      return { type: 'overlayStep', delta: -1 };
    default:
      return null;
  }
};

// The mirror of PANELS_NEEDING_NODE: panels that exist ONLY in the family
// sidebar. A shortcut that reaches one has to drop the selection first —
// leaving a person selected would expand a panel that view does not render,
// i.e. do nothing at all.
//
// EMPTY since TGCLOUD-1674: its two members, LEGEND and LABELS, left the
// sidebar entirely for modals on the pedigree panel. Kept (with the handling in
// ChartBox and the invariants below) because the trap it guards is a property
// of the sidebar's two views, not of those two panels — the next family-only
// panel needs the same treatment.
export const FAMILY_ONLY_PANELS = new Set();

export const PANELS_NEEDING_NODE = new Set([
  'diagnoses',
  'genotypes',
  'phenotypes',
  'riskFactors',
  'suggestions',
  'formSubmission',
  'recordStatus'
]);

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '');
export const MOD_KEY_LABEL = isMac ? '⌘' : 'Ctrl';

/**
 * The binding for each add, keyed by the SAME `relative` vocabulary `resolveShortcut`
 * emits and the `addRelative` handler switches on.
 *
 * One source, three consumers: the `?` overlay (via SHORTCUT_GROUPS below), the
 * context menu's Add Relative rows, and the sidebar's Add Relative tiles. Before
 * TGCLOUD-1642's footnote was picked up, the keys existed only here and in the
 * overlay — every add had a binding and nothing in the app said so.
 *
 * `keyboardShortcuts.test.js` asserts every entry round-trips through
 * `resolveShortcut` to the matching `relative`, so an advertised key is provably the
 * key that fires. That test is the whole point of this being a shared map rather
 * than a second list of strings.
 *
 * ORDER MATTERS: SHORTCUT_GROUPS renders the overlay's "Add relatives" group from
 * this object's insertion order, and it is the order the overlay has always shown.
 */
export const ADD_RELATIVE_KEYS = Object.freeze({
  son: ['M'],
  daughter: ['F'],
  unknown: ['U'],
  spouse: ['P'],
  brother: ['B'],
  sister: ['S'],
  parents: ['A'],
  grandparents: ['Shift', 'A']
});

// Overlay wording, which is deliberately fuller than a menu row's label ("Add
// individual (unknown sex)" vs "Unknown person") — the overlay is read cold, a menu
// row is read next to its own notation.
const ADD_RELATIVE_LABELS = {
  son: 'Add son',
  daughter: 'Add daughter',
  unknown: 'Add individual (unknown sex)',
  spouse: 'Add partner / spouse',
  brother: 'Add brother',
  sister: 'Add sister',
  parents: 'Add parents',
  grandparents: 'Add grandparents'
};

export const SHORTCUT_GROUPS = [
  {
    title: 'Add relatives',
    // hint: 'with a family member selected',
    items: Object.entries(ADD_RELATIVE_KEYS).map(([relative, keys]) => ({
      keys,
      label: ADD_RELATIVE_LABELS[relative]
    }))
  },
  {
    title: 'Panels',
    items: [
      { keys: ['D'], label: 'Diseases' },
      { keys: ['N'], label: 'Clinical notes' },
      { keys: ['G'], label: 'Genotypes' },
      { keys: ['H'], label: 'Phenotypes' },
      { keys: ['R'], label: 'Risk scores' },
      { keys: ['I'], label: 'Clinical insights' },
      // { keys: ['O'], label: 'Form submissions' },
      { keys: ['W'], label: 'Pathways' },
      // { keys: ['Y'], label: 'Pedigree analysis' },
      // { keys: ['C'], label: 'Record status' },
      { keys: ['L'], label: 'Legend' }
      // { keys: ['E'], label: 'Export' }
    ]
  },
  {
    title: 'Edit & select',
    items: [
      { keys: ['F2'], label: 'Edit person / name' },
      { keys: ['Delete'], label: 'Delete selected' },
      { keys: [MOD_KEY_LABEL, 'A'], label: 'Select all' },
      { keys: ['Esc'], label: 'Clear selection' },
      { keys: ['↑', '↓', '←', '→'], label: 'Nudge selected' }
    ]
  },
  {
    title: 'View',
    items: [
      { keys: ['+'], label: 'Zoom in' },
      { keys: ['−'], label: 'Zoom out' },
      { keys: ['Scroll'], label: 'Zoom' },
      { keys: [MOD_KEY_LABEL, 'G'], label: 'Toggle grid' },
      { keys: ['V'], label: 'Overlays' },
      { keys: ['J'], label: 'Next overlay match' },
      { keys: ['K'], label: 'Previous overlay match' },
      { keys: ['?'], label: 'Show this help' }
    ]
  }
];
